import Swal from "sweetalert2"

// Server-authoritative pricing: the backend recomputes every booking price from the database
// and rejects the booking when the price this browser sent does not match. These are the codes
// it can reject with. All of them mean "do not trust the client price any more".
const MISMATCH_CODES = [
  "PRICE_MISMATCH",
  "ROUTE_MISMATCH",
  "LEG_TOTAL_MISMATCH",
  "CURRENCY_MISMATCH",
  "PRICE_UNVERIFIED",
]

export const isServerPriceCorrection = (response) =>
  Boolean(response && response.success === false && MISMATCH_CODES.includes(response.code))

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return null
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const hasServerPrice = (data) =>
  data?.serverTotalFare !== null &&
  data?.serverTotalFare !== undefined &&
  Number.isFinite(Number(data.serverTotalFare))

/**
 * Rewrites the payload money fields with the server values. The backend stays the authority -
 * these values are informational - but sending them back lets the retried request pass the
 * server's own price check. Pure: no UI, so it can be unit tested.
 */
export const applyServerPriceToPayload = (payload, data = {}) => {
  const serverTotal = data.serverTotalFare
  const serverBase = data.serverBaseFare
  const selectedType = data.selectedPricingType || payload?.snapshot?.selectedPricingType
  const serverPricing = data.pricing

  if (hasServerPrice(data)) payload.totalFare = Number(serverTotal)
  if (serverBase !== null && serverBase !== undefined) payload.baseFare = Number(serverBase)
  if (data.currencyId) payload.currency = data.currencyId
  if (serverPricing && payload.snapshot && selectedType) {
    payload.snapshot.pricing = payload.snapshot.pricing || {}
    payload.snapshot.pricing[selectedType] = serverPricing
    payload.snapshot.selectedPricingType = selectedType
  }
  return payload
}

/**
 * Handles a server price rejection.
 *
 * - The server recomputed a different price: show it and, if the customer confirms, rewrite the
 *   payload money fields with the server values so the retry passes the server's own check.
 * - The server could not verify a price at all (PRICE_UNVERIFIED): explain and do NOT retry -
 *   there is nothing to re-confirm, and retrying would just fail again.
 *
 * @returns {Promise<boolean>} true when the caller should retry with the mutated payload.
 */
export const applyServerPriceCorrection = async (response, payload) => {
  const data = response?.data || {}

  if (!hasServerPrice(data)) {
    await Swal.fire({
      icon: "error",
      title: "Price could not be verified",
      text:
        response?.message ||
        "The server could not verify this price. No booking was created. Please refresh the trip and try again.",
      confirmButtonColor: "#0ea5e9",
    })
    return false
  }

  const totalText = formatAmount(data.serverTotalFare)
  const bodyText = totalText
    ? `Price does not match server. The updated total is ${totalText}.`
    : response?.message || "Price does not match server. The price has been updated."

  const result = await Swal.fire({
    icon: "warning",
    title: "Price updated",
    text: bodyText,
    showCancelButton: true,
    confirmButtonText: "Confirm new price",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#0ea5e9",
    cancelButtonColor: "#6c757d",
  })

  if (!result.isConfirmed) {
    await Swal.fire({
      icon: "info",
      title: "Booking not created",
      text: "No booking was created. Please review the updated price before trying again.",
      confirmButtonColor: "#0ea5e9",
    })
    return false
  }

  applyServerPriceToPayload(payload, data)
  return true
}